import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  TabList,
  Tab,
  Tooltip,
  Spinner,
  mergeClasses,
  Link,
} from '@fluentui/react-components';
import {
  CopyFilled,
  CheckmarkFilled,
  ChevronLeftRegular,
  ChevronRightRegular,
  TextBulletListSquareRegular,
  BotSparkleFilled,
  OpenRegular,
  ArrowExpandRegular,
} from '@fluentui/react-icons';
import type { NodeExecution } from '@/types/monitoring';
import type { RepetitionInfo, AgentIterationInfo, ChatMessageEntry, AgentToolActionInfo } from '@/services/monitoringApi';
import type { WorkflowDefinition } from '@/types/workflowDefinition';
import { isScopeNodeType } from './ExecutionListItem';
import { ExecutionLog, type ExecutionLogProps } from './ExecutionLog';
import { isAgentExecution } from '@/utils/monitoring';
import { useIterationData } from '@/hooks/useMonitoringData';
import { formatDateTime, formatDuration } from '@/utils/dateTime';
import { IterationSelector } from '@/components/MonitoringView/ActionDetailPanel/IterationSelector';
import { JsonViewer } from '@/components/MonitoringView/ActionDetailPanel/JsonViewer';
import { RetryHistory } from '@/components/MonitoringView/ActionDetailPanel/RetryHistory';
import { MonitoringExpandDialog } from '@/components/MonitoringView/MonitoringExpandDialog';
import { useTelemetry } from '@/hooks/useTelemetry';
import { sortNodeExecutions, buildScopeParentMap } from '@/utils/workflow';
import { formatChatTimestamp } from '@/utils/dateTime';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { ottoTokens } from '@/theme/ottoTokens';
import { NodeStatusBadge } from '@/components/MonitoringView/shared/NodeStatusBadge';

const FOUNDRY_FALLBACK_URL = 'https://ai.azure.com';

/**
 * Returns `candidate` only when it parses as an http(s) URL. Anything
 * else (relative URL, `javascript:`, `data:`, `vbscript:`, …) is
 * rejected and the trusted fallback is returned instead. Used to harden
 * any external link whose target is sourced from runtime data — without
 * this guard, a crafted `foundryPortalUrl` of `javascript:alert(1)`
 * would execute when the user clicks the Foundry link.
 */
const safeExternalUrl = (candidate: string | undefined, fallback: string): string => {
  if (!candidate) return fallback;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return candidate;
  } catch {
    // not a valid absolute URL — fall through to fallback
  }
  return fallback;
};

/** Selected chat message info for displaying in detail panel */
type SelectedChatMessage = {
  role: 'User' | 'Assistant';
  content: string;
  timestamp: string;
  iteration?: number | undefined;
};

type LogsPanelProps = {
  nodeExecutions: NodeExecution[];
  selectedNodeExecution: NodeExecution | null;
  selectedNodeId: string | null;
  onNodeSelect: (nodeId: string, iterationIndex?: number, toolId?: string) => void;
  /** Currently selected iteration index for the selected agent node */
  selectedIterationIndex?: number | undefined;
  /** Currently selected tool ID within the iteration */
  selectedToolId?: string | undefined;
  /**
   * Whether this is a conversational workflow (kind: "Agent").
   * - true: User message brackets the entire turn (User → all executions → Response)
   * - false: Autonomous workflow where messages are per-agent (Trigger → Actions → [Agent: User → Iterations → Response])
   */
  isConversational?: boolean | undefined;
  /** Callback when iteration changes (for updating canvas) */
  onIterationChange?: ((nodeId: string, iterationIndex: number) => void) | undefined;
  isLoadingRun?: boolean | undefined;
  isLoadingDetails?: boolean | undefined;
  /** Whether the panel is collapsed */
  isCollapsed?: boolean | undefined;
  /** Called when collapse state should change */
  onCollapseChange?: ((collapsed: boolean) => void) | undefined;
  /** Callback to fetch repetitions for a scope node */
  onFetchRepetitions?: ((scopeName: string) => void) | undefined;
  /** Map of scope name to repetitions (loaded lazily) */
  scopeRepetitions?: Map<string, RepetitionInfo[]> | undefined;
  /** Set of scope names currently loading */
  loadingScopeRepetitions?: Set<string> | undefined;
  /** Currently selected repetition name */
  selectedRepetitionName?: string | undefined;
  /** Callback when a repetition is selected */
  onRepetitionSelect?: ((scopeName: string, repetitionName: string) => void) | undefined;
  /** Map of scope-repetition key to actions within that repetition */
  scopeActions?: Map<string, NodeExecution[]> | undefined;
  /** Set of scope-repetition keys currently loading actions */
  loadingScopeActions?: Set<string> | undefined;
  /** Callback to fetch actions within a scope repetition */
  onFetchScopeActions?: ((scopeName: string, repetitionName: string) => void) | undefined;
  /** Callback to fetch agent iterations when an agent node is expanded */
  onFetchAgentIterations?: ((agentName: string) => void) | undefined;
  /** Map of agent node name to iterations (loaded lazily) */
  agentIterations?: Map<string, AgentIterationInfo[]> | undefined;
  /** Set of agent names currently loading iterations */
  loadingAgentIterations?: Set<string> | undefined;
  /** Chat history messages (for agentic workflows) */
  chatHistory?: ChatMessageEntry[] | undefined;
  /** Map of iteration key (agentName-iterationName) to tool actions */
  toolActions?: Map<string, AgentToolActionInfo[]> | undefined;
  /** Set of iteration keys currently loading tool actions */
  loadingToolActions?: Set<string> | undefined;
  /** Callback to fetch tool actions for an iteration */
  onFetchToolActions?: ((agentName: string, iterationName: string) => void) | undefined;
  /** Current iteration index per agent shown on canvas (keyed by agent name) */
  canvasAgentIterations?: Record<string, number> | undefined;
  /** Current iteration index per scope shown on canvas (keyed by scope name) */
  canvasScopeIterations?: Record<string, number> | undefined;
  /** Workflow definition for proper action ordering (falls back to timestamp sorting if not provided) */
  workflowDefinition?: WorkflowDefinition | undefined;
  // === ChatMessage Iteration Actions (OMT workflows) ===
  // Now uses NodeExecution[] - the SAME type as stateful workflows!
  // This enables full reuse of stateful monitoring components for OMT per-message.
  // NOTE: Non-OMT Agent workflows (kind: Agent with A2A) still use agentIterations/toolActions.
  /** Map of iteration index to NodeExecutions for OMT chatMessage workflows */
  chatMessageIterationActions?: Map<number, NodeExecution[]> | undefined;
  /** Set of iteration indices currently loading */
  loadingChatMessageIterations?: Set<number> | undefined;
  /** Callback to fetch actions for a chatMessage iteration */
  onFetchChatMessageIterationActions?: ((iterationIndex: number) => void) | undefined;
  /** Pre-built Foundry portal URL for the selected agent action */
  foundryPortalUrl?: string | undefined;
  /** Current run ID for fetching action repetition details */
  runId?: string | undefined;
};

type TabValue = 'input' | 'output' | 'properties' | 'retry';

const COLLAPSED_WIDTH = 32;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    flex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
  },
  // Each column owns its own header + body. Stacking them this way lets
  // the resize divider between columns span the full height of the panel
  // (header included), which matches the polished mockup.
  column: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  // The details column fills the remaining horizontal space next to the
  // resizable list column. Kept on its own modifier so the resize divider
  // doesn't accidentally claim flex space.
  detailsColumn: {
    flex: 1,
    minWidth: 0,
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
    // Both column headers share this row. The right column contains a
    // Fluent small `TabList` whose intrinsic height (tab text + active
    // indicator) is taller than the left column's title + icon button,
    // which made the two bottom borders end at different y-coordinates.
    // Anchor a fixed height so they always line up exactly — the inner
    // content is vertically centered via `alignItems: center`.
    height: '48px',
    boxSizing: 'border-box',
  },
  // Collapsed state - thin vertical bar
  collapsedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: `${COLLAPSED_WIDTH}px`,
    minWidth: `${COLLAPSED_WIDTH}px`,
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground3,
    borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingTop: tokens.spacingVerticalS,
    gap: tokens.spacingVerticalS,
  },
  collapsedIcon: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase400,
  },
  collapsedTitle: {
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expandButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  headerCount: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  collapseButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  // Left side: execution list - width controlled by draggable splitter
  executionList: {
    flexShrink: 0,
    overflow: 'auto',
    backgroundColor: tokens.colorNeutralBackground2,
    ...customScrollbarStyles,
  },
  // Draggable splitter/divider between list and details
  resizeDivider: {
    width: '4px',
    cursor: 'col-resize',
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground,
    },
  },
  resizeDividerActive: {
    backgroundColor: tokens.colorBrandBackground,
  },
  // Row wrapper for execution item (allows expand button as sibling to main button)
  executionItemRow: {
    display: 'flex',
    alignItems: 'center',
  },
  // Execution item in list
  executionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: tokens.spacingVerticalXS,
    paddingRight: tokens.spacingHorizontalS,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    borderLeft: '3px solid transparent',
    transitionProperty: 'all',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
  },
  // Failed item - red left border to highlight failure path
  executionItemFailed: {
    borderLeftColor: tokens.colorPaletteRedBorder1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    ':hover': {
      backgroundColor: tokens.colorPaletteRedBackground1,
    },
  },
  // Skipped item - reduced opacity to de-emphasize
  executionItemSkipped: {
    opacity: 0.6,
    ':hover': {
      opacity: 0.8,
    },
  },
  executionItemTrigger: {
    paddingLeft: tokens.spacingHorizontalXS,
  },
  executionItemAction: {
    paddingLeft: tokens.spacingHorizontalXS,
  },
  // Agent nodes that have iterations - show expand/collapse
  executionItemAgent: {
    paddingLeft: tokens.spacingHorizontalXS,
  },
  // Iteration items (nested under agent)
  executionItemIteration: {
    paddingLeft: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // Tool items (nested under iteration)
  executionItemTool: {
    paddingLeft: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase100,
  },
  // Tool action items (nested under tool, e.g., "Get current weather" under "Tool_2")
  executionItemToolAction: {
    paddingLeft: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
  },
  // Scope items (forEach, Until, etc.)
  executionItemScope: {
    paddingLeft: tokens.spacingHorizontalXS,
  },
  // Repetition items (nested under scope)
  executionItemRepetition: {
    paddingLeft: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // Scope icon styling
  scopeIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginRight: tokens.spacingHorizontalXS,
  },
  // Repetition count badge
  repetitionBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground4,
    padding: `1px ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    marginLeft: 'auto',
  },
  // Chat message items
  executionItemChatMessage: {
    paddingLeft: tokens.spacingHorizontalS,
  },
  chatMessageIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginRight: tokens.spacingHorizontalXS,
  },
  chatMessageRole: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  chatMessageTime: {
    marginLeft: 'auto',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  // Loading indicator in tree
  loadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingLeft: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  // Fixed-width slot reserved for the expand chevron. Always renders on
  // non-trigger rows so the status icon column stays vertically aligned
  // whether or not the row is expandable. Width matches Fluent's icon-only
  // small Button so chevron placement is identical to the empty slot.
  treeExpandSlot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '28px',
    height: '24px',
  },
  // Expand/collapse button for agent tree nodes
  treeExpandButton: {
    minWidth: tokens.spacingHorizontalL,
    width: tokens.spacingHorizontalL,
    height: tokens.spacingHorizontalL,
    padding: '0',
    marginRight: tokens.spacingHorizontalXXS,
    '& svg': {
      fontSize: tokens.fontSizeBase200,
    },
  },
  // Iteration header styling
  iterationHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  iterationIndex: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  iterationAgentName: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  iterationCount: {
    color: tokens.colorNeutralForeground3,
  },
  // Nested group indentation for hierarchical tree (iterations under agents)
  nestedGroup: {
    marginLeft: '18px', // Align with parent chevron button (16px + 2px margin)
    paddingLeft: tokens.spacingHorizontalXS,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Double nested group (tools under iterations)
  nestedGroupDeep: {
    marginLeft: tokens.spacingHorizontalM, // Smaller indent for tools under iterations
    paddingLeft: tokens.spacingHorizontalXS,
    borderLeft: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  // Vertical loop-body rail to visually nest a ForEach iteration's inner
  // actions. Aligns with the iteration row's status icon.
  iterationBody: {
    position: 'relative',
    marginLeft: tokens.spacingHorizontalXL,
    paddingLeft: tokens.spacingHorizontalS,
    '::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '4px',
      bottom: '4px',
      width: '1.5px',
      backgroundColor: tokens.colorPalettePurpleForeground2,
      opacity: 0.55,
      borderRadius: '1px',
      '@media (forced-colors: active)': {
        backgroundColor: 'CanvasText',
        opacity: 1,
      },
    },
  },
  // Chevron button on the iteration row to indicate open/closed state.
  iterationChev: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  // "AFTER LOOP" divider that separates a ForEach scope from the top-level
  // post-loop actions in the execution list.
  afterLoopDivider: {
    display: 'block',
    margin: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS} 0`,
    paddingTop: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Iteration count badge (e.g., "3 iter")
  iterationBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
    marginRight: tokens.spacingHorizontalS,
  },
  // Duration badge inline
  durationBadge: {
    marginLeft: 'auto',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontFamily: 'monospace',
  },
  executionItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandStroke1,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  // Highlight animation when selection changes from canvas
  executionItemHighlight: {
    animationName: {
      '0%': { backgroundColor: tokens.colorBrandBackground },
      '100%': { backgroundColor: tokens.colorBrandBackground2 },
    },
    animationDuration: '0.5s',
    animationTimingFunction: 'ease-out',
  },
  statusIcon: {
    flexShrink: 0,
    width: '14px',
    height: '14px',
    fontSize: tokens.fontSizeBase300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusSucceeded: {
    color: ottoTokens.statusSucceeded,
  },
  statusFailed: {
    color: ottoTokens.statusFailed,
  },
  statusRunning: {
    color: ottoTokens.statusRunning,
  },
  statusSkipped: {
    color: ottoTokens.statusSkipped,
  },
  statusCancelled: {
    color: ottoTokens.statusCancelled,
  },
  statusPending: {
    color: ottoTokens.statusPending,
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemNameSelected: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  tabsLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  tabsRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  durationText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontFamily: 'monospace',
  },
  // Content area
  content: {
    flex: 1,
    overflow: 'auto',
    padding: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    // Flex column so the JsonViewer host can stretch to fill the pane
    // (it owns its own scroll); other tab content (Properties, empty
    // states, error states) renders as normal block children.
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    ...customScrollbarStyles,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: tokens.colorNeutralForeground3,
    gap: tokens.spacingVerticalXS,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    width: '100%',
    height: '100%',
    gap: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
  },
  jsonContainer: {
    position: 'relative',
  },
  // Host wrapper that lets the JsonViewer fill the tab pane and own its
  // own scroll. Keeps the same look-and-feel as the dialog's panes.
  jsonViewerHost: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
  },
  jsonViewer: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: tokens.colorNeutralForeground1,
    ...customScrollbarStyles,
  },
  copyButton: {
    position: 'absolute',
    top: tokens.spacingVerticalS,
    right: tokens.spacingHorizontalS,
  },
  propertiesGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: tokens.spacingVerticalS,
    rowGap: tokens.spacingVerticalM,
    alignItems: 'center',
    justifyItems: 'start',
  },
  propertyLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  propertyValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    fontFamily: 'monospace',
  },
  errorSection: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
  },
  errorTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
  },
  foundryBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  foundryIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorPalettePurpleForeground2,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  foundryText: {
    flex: 1,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  foundryLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  chatMessageHeader: {
    marginBottom: tokens.spacingVerticalS,
  },
  chatMessageTimestamp: {
    marginLeft: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  errorCodeText: {
    color: tokens.colorPaletteRedForeground1,
  },
  preWrapText: {
    whiteSpace: 'pre-wrap',
  },
  emptyStateIconSpacing: {
    marginBottom: tokens.spacingVerticalS,
  },
});

const formatFullDateTime = (timestamp?: string): string =>
  formatDateTime(timestamp, 'weekday', { seconds: true, fallback: 'N/A' });

const formatLogsDuration = (duration?: number): string =>
  formatDuration(duration, { style: 'execution', fallback: '--' });

// Scope node types that can have repetitions
const SCOPE_NODE_TYPES = ['Foreach', 'Until', 'Scope', 'If', 'Switch'];

const isScopeNode = (nodeType: string): boolean => {
  return SCOPE_NODE_TYPES.some(type => nodeType.toLowerCase().includes(type.toLowerCase()));
};

/**
 * Detects if this is an OMT chatMessage workflow by checking for the internal orchestrator.
 * ChatMessage workflows use internal.MessageIterationOrchestrator to manage multi-turn messages.
 */
const checkIsChatMessageWorkflow = (executions: NodeExecution[]): boolean => {
  return executions.some(e => e.nodeName === 'internal.MessageIterationOrchestrator');
};

export const LogsPanel = ({
  nodeExecutions,
  selectedNodeExecution,
  selectedNodeId,
  onNodeSelect,
  selectedIterationIndex,
  selectedToolId,
  onIterationChange,
  isLoadingRun = false,
  isLoadingDetails = false,
  isCollapsed = false,
  onCollapseChange,
  onFetchRepetitions,
  scopeRepetitions,
  loadingScopeRepetitions,
  selectedRepetitionName,
  onRepetitionSelect,
  scopeActions,
  loadingScopeActions,
  onFetchScopeActions,
  onFetchAgentIterations,
  agentIterations,
  loadingAgentIterations,
  chatHistory,
  toolActions,
  loadingToolActions,
  onFetchToolActions,
  canvasAgentIterations,
  canvasScopeIterations,
  chatMessageIterationActions,
  loadingChatMessageIterations,
  onFetchChatMessageIterationActions,
  foundryPortalUrl,
  isConversational = false,
  runId,
  workflowDefinition,
}: LogsPanelProps) => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const [selectedTab, setSelectedTab] = useState<TabValue>('output');
  const [copied, setCopied] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());
  const [expandedScopes, setExpandedScopes] = useState<Set<string>>(new Set());
  // Whether the expand-to-dialog overlay is showing.
  const [isExpandOpen, setIsExpandOpen] = useState(false);
  // Map of scope name → set of repetition names that are expanded.
  // Iterations are collapsed by default; only the selected (or user-clicked)
  // iteration expands to reveal its inner action list. This mirrors the
  // Option A "nested rail" mockup.
  const [expandedRepetitions, setExpandedRepetitions] = useState<Map<string, Set<string>>>(new Map());
  // Track whether we've already done the initial auto-expand for ForEach
  // scopes so we don't fight the user when they manually collapse them.
  const hasInitiallyExpandedForeachRef = useRef(false);
  // Selected chat message for detail panel display
  const [selectedChatMessage, setSelectedChatMessage] = useState<SelectedChatMessage | null>(null);
  // Selected tool action for detail panel display
  const [selectedToolAction, setSelectedToolAction] = useState<AgentToolActionInfo | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Resizable list width state
  const [listWidth, setListWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  // Resize handlers for draggable splitter
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = listWidth;
  }, [listWidth]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX.current;
      const newWidth = Math.min(500, Math.max(200, resizeStartWidth.current + delta));
      setListWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Resolve the scope that owns the currently-selected action's iterations.
  // - Selected node is a ForEach/Until scope → the scope itself.
  // - Selected node is a child action inside a scope → its parent.
  // Used to keep IterationSelector (right detail panel) and the scope's
  // iteration row highlight (left list) in sync via onRepetitionSelect.
  const ownerScopeName = useMemo(() => {
    if (!selectedNodeExecution) return null;
    if (selectedNodeExecution.parentNodeId) return selectedNodeExecution.parentNodeId;
    if (isScopeNode(selectedNodeExecution.nodeType)) return selectedNodeExecution.nodeName;
    return null;
  }, [selectedNodeExecution]);

  // Iteration data for ForEach inner actions in the detail panel.
  // Pass the externally-controlled repetition name so the hook honors the
  // selection from LogsPanel rows / the canvas scrubber, and a callback so
  // changes from the IterationSelector cards lift up into the same shared
  // state (LogsPanel highlight + canvas pager).
  const iteration = useIterationData(selectedNodeExecution, runId ?? null, {
    enabled: !selectedToolAction,
    nodeExecutions,
    externalRepetitionName: ownerScopeName ? selectedRepetitionName : undefined,
    onIterationChange: ownerScopeName
      ? (repName) => onRepetitionSelect?.(ownerScopeName, repName)
      : undefined,
  });

  // Reset internal selection state when switching runs (selectedNodeId becomes null)
  useEffect(() => {
    if (selectedNodeId === null) {
      setSelectedChatMessage(null);
      setSelectedToolAction(null);
      setExpandedAgents(new Set());
      setExpandedScopes(new Set());
      setHighlightedId(null);
    }
  }, [selectedNodeId]);

  // Toggle agent expansion and fetch iterations if needed
  const toggleAgentExpanded = useCallback((nodeId: string, agentName: string) => {
    setExpandedAgents(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
        // Fetch agent iterations when expanding if not already loaded
        if (!agentIterations?.has(agentName) && onFetchAgentIterations) {
          onFetchAgentIterations(agentName);
        }
      }
      return next;
    });
  }, [agentIterations, onFetchAgentIterations]);

  // Toggle scope expansion and fetch repetitions if needed
  const toggleScopeExpanded = useCallback((scopeName: string, nodeType?: string) => {
    setExpandedScopes(prev => {
      const next = new Set(prev);
      if (next.has(scopeName)) {
        next.delete(scopeName);
      } else {
        next.add(scopeName);
        // Fetch repetitions when expanding if not already loaded
        // Skip for Until scopes - the runtime doesn't support scopeRepetitions for Until;
        // child actions are shown from nodeExecutions instead
        const isUntil = nodeType?.toLowerCase().includes('until');
        if (!isUntil && !scopeRepetitions?.has(scopeName) && onFetchRepetitions) {
          onFetchRepetitions(scopeName);
        }
      }
      return next;
    });
  }, [scopeRepetitions, onFetchRepetitions]);

  // Auto-expand running agents so users can see iteration progress
  useEffect(() => {
    const runningAgentNodeIds = nodeExecutions
      .filter((exec) => {
        const isRunning = exec.status === 'running';
        const hasIterations = (exec.iterationCount ?? 0) > 0;
        return isAgentExecution(exec) && (isRunning || hasIterations);
      })
      .map((exec) => exec.nodeId);

    if (runningAgentNodeIds.length > 0) {
      setExpandedAgents((prev) => {
        const next = new Set(prev);
        for (const nodeId of runningAgentNodeIds) {
          next.add(nodeId);
        }
        return next;
      });
    }
  }, [nodeExecutions]);

  // Auto-expand ForEach scopes on first render so users see their loop body
  // immediately. Only runs once — after that, expansion is user-controlled.
  useEffect(() => {
    if (hasInitiallyExpandedForeachRef.current) return;
    const foreachScopeNames = nodeExecutions
      .filter((exec) => isScopeNodeType(exec.nodeType) && exec.nodeType.toLowerCase().includes('foreach'))
      .map((exec) => exec.nodeName);
    if (foreachScopeNames.length === 0) return;
    hasInitiallyExpandedForeachRef.current = true;
    setExpandedScopes((prev) => {
      const next = new Set(prev);
      for (const name of foreachScopeNames) next.add(name);
      return next;
    });
  }, [nodeExecutions]);

  // Hydrate the iteration list for any expanded ForEach scope that hasn't been
  // fetched yet. Decoupled from the one-shot auto-expand so it survives prop
  // arrival order (nodeExecutions vs. scopeRepetitions can populate at
  // different times) and fires when the user manually expands a scope too.
  useEffect(() => {
    if (!onFetchRepetitions) return;
    for (const exec of nodeExecutions) {
      if (!isScopeNodeType(exec.nodeType)) continue;
      if (!exec.nodeType.toLowerCase().includes('foreach')) continue;
      if (!expandedScopes.has(exec.nodeName)) continue;
      if (scopeRepetitions?.has(exec.nodeName)) continue;
      if (loadingScopeRepetitions?.has(exec.nodeName)) continue;
      onFetchRepetitions(exec.nodeName);
    }
  }, [expandedScopes, nodeExecutions, scopeRepetitions, loadingScopeRepetitions, onFetchRepetitions]);

  // Index nodeExecutions by parentNodeId once per nodeExecutions change so the
  // per-iteration fallback action list is O(1) instead of O(iters * nodes).
  const childrenByScope = useMemo(() => {
    const map = new Map<string, NodeExecution[]>();
    for (const exec of nodeExecutions) {
      if (!exec.parentNodeId) continue;
      const list = map.get(exec.parentNodeId);
      if (list) {
        list.push(exec);
      } else {
        map.set(exec.parentNodeId, [exec]);
      }
    }
    return map;
  }, [nodeExecutions]);

  // Toggle expansion of an individual iteration row inside a scope.
  const toggleRepetitionExpanded = useCallback((scopeName: string, repetitionName: string) => {
    setExpandedRepetitions((prev) => {
      const next = new Map(prev);
      const inner = new Set(next.get(scopeName) ?? []);
      if (inner.has(repetitionName)) {
        inner.delete(repetitionName);
      } else {
        inner.add(repetitionName);
      }
      next.set(scopeName, inner);
      return next;
    });
  }, []);

  // Auto-expand the iteration that matches selectedRepetitionName so that
  // selecting via the right-side iteration scrubber opens the matching row
  // on the left-side log.
  useEffect(() => {
    if (!selectedRepetitionName) return;
    // Find which scope owns this repetition by scanning scopeRepetitions.
    if (!scopeRepetitions) return;
    for (const [scopeName, reps] of scopeRepetitions.entries()) {
      if (reps.some((r) => r.name === selectedRepetitionName)) {
        setExpandedRepetitions((prev) => {
          const inner = prev.get(scopeName);
          if (inner?.has(selectedRepetitionName)) return prev;
          const next = new Map(prev);
          const innerNext = new Set(inner ?? []);
          innerNext.add(selectedRepetitionName);
          next.set(scopeName, innerNext);
          return next;
        });
        break;
      }
    }
  }, [selectedRepetitionName, scopeRepetitions]);

  // Auto-fetch tool actions for expanded agents with iterations
  useEffect(() => {
    if (!onFetchToolActions || !agentIterations) return;

    // For each expanded agent, fetch tool actions for ALL iterations
    // We fetch for all iterations because built-in tools like code_interpreter
    // may only appear in the detailed actions API, not in the initial iterations list
    for (const nodeId of expandedAgents) {
      const exec = nodeExecutions.find(e => e.nodeId === nodeId);
      if (!exec) continue;

      const agentName = exec.nodeName;
      const iterations = agentIterations.get(agentName) ?? [];

      for (const iteration of iterations) {
        // Ensure this is an AgentIterationInfo type with a name property
        if (!('name' in iteration)) continue;

        const iterationName = iteration.name;
        const iterationKey = `${agentName}-${iterationName}`;

        // Only fetch if not already loaded or loading
        if (!toolActions?.has(iterationKey) && !loadingToolActions?.has(iterationKey)) {
          onFetchToolActions(agentName, iterationName);
        }
      }
    }
  }, [expandedAgents, nodeExecutions, agentIterations, toolActions, loadingToolActions, onFetchToolActions]);

  // Otto's runtime exposes `/actions/{childName}/repetitions/{repName}` per
  // child action of a ForEach scope. The scope-level `/scopeRepetitions`
  // route works (and we use it to populate the iteration list), but a
  // per-iteration child fan-out endpoint is not exposed — so when the user
  // expands an iteration row below we fan out per child instead, calling
  // `onFetchScopeActions` on demand rather than speculatively for every
  // iteration at once.

  // Sort executions by workflow definition order (with fallback to start time)
  // This ensures actions appear in the order they're defined in the workflow,
  // not alphabetically or by arbitrary runtime execution time (which breaks parallel branches)
  const sortedExecutions = useMemo(() => {
    return sortNodeExecutions(nodeExecutions, workflowDefinition);
  }, [nodeExecutions, workflowDefinition]);

  /**
   * Map of child action name → immediate scope parent name. Built from the
   * workflow definition because the runtime only sets `parentNodeId` on
   * ForEach/Until inner actions, leaving If/Switch/Scope children without a
   * parent pointer. Passed through to ExecutionLog so it can indent nested
   * actions in the flat list.
   */
  const scopeParentByName = useMemo(
    () => buildScopeParentMap(workflowDefinition?.definition?.actions),
    [workflowDefinition?.definition?.actions],
  );

  // Filter chat messages to show User and Assistant content messages (not tool results)
  // For multi-turn conversations, we group user messages with their corresponding agent responses
  const contentMessages = useMemo(() => {
    if (!chatHistory) return [];
    return chatHistory.filter(
      (msg) => msg.messageEntryType === 'Content' && (msg.role === 'User' || msg.role === 'Assistant')
    );
  }, [chatHistory]);

  // Group chat messages into conversation turns (user message + agent response pairs)
  // Each turn represents one round of user input -> agent processing -> agent response
  const conversationTurns = useMemo(() => {
    type ConversationTurn = {
      userMessage: ChatMessageEntry | undefined;
      assistantMessage: ChatMessageEntry | undefined;
      iteration: number;
      taskSequenceId?: string;
    };
    const turns: ConversationTurn[] = [];

    // Check if all messages have the same iteration (chatMessage workflow case)
    // In this case, we need to group by taskSequenceId from agentMetadata
    const allSameIteration = contentMessages.every(
      (msg) => (msg.iteration ?? 0) === (contentMessages[0]?.iteration ?? 0)
    );
    const hasDistinctTaskSequenceIds = new Set(
      contentMessages.map((msg) => (msg as { agentMetadata?: { taskSequenceId?: string } }).agentMetadata?.taskSequenceId).filter(Boolean)
    ).size > 1;
    
    // Use taskSequenceId grouping for chatMessage workflows where iteration is not set per-turn
    if (allSameIteration && hasDistinctTaskSequenceIds && contentMessages.length > 2) {
      // Group by taskSequenceId - each unique taskSequenceId represents a turn
      const byTaskSequenceId = new Map<string, { user?: ChatMessageEntry; assistant?: ChatMessageEntry; timestamp: Date }>();
      for (const msg of contentMessages) {
        const taskSeqId = (msg as { agentMetadata?: { taskSequenceId?: string } }).agentMetadata?.taskSequenceId ?? 'default';
        if (!byTaskSequenceId.has(taskSeqId)) {
          byTaskSequenceId.set(taskSeqId, { timestamp: new Date(msg.timestamp) });
        }
        const entry = byTaskSequenceId.get(taskSeqId)!;
        if (msg.role === 'User') {
          entry.user = msg;
        } else if (msg.role === 'Assistant') {
          entry.assistant = msg;
        }
      }

      // Convert to array sorted by timestamp
      const sortedTasks = Array.from(byTaskSequenceId.entries())
        .filter(([, msgs]) => msgs.user || msgs.assistant)
        .sort((a, b) => a[1].timestamp.getTime() - b[1].timestamp.getTime());
      
      sortedTasks.forEach(([taskSeqId, messages], index) => {
        turns.push({
          userMessage: messages.user,
          assistantMessage: messages.assistant,
          iteration: index,
          taskSequenceId: taskSeqId,
        });
      });
    } else {
      // Standard iteration-based grouping
      const byIteration = new Map<number, { user?: ChatMessageEntry; assistant?: ChatMessageEntry }>();
      for (const msg of contentMessages) {
        const iter = msg.iteration ?? 0;
        if (!byIteration.has(iter)) {
          byIteration.set(iter, {});
        }
        const entry = byIteration.get(iter)!;
        if (msg.role === 'User') {
          entry.user = msg;
        } else if (msg.role === 'Assistant') {
          entry.assistant = msg;
        }
      }

      // Convert to array sorted by iteration
      const sortedIterations = Array.from(byIteration.entries()).sort((a, b) => a[0] - b[0]);
      for (const [iteration, messages] of sortedIterations) {
        turns.push({
          userMessage: messages.user,
          assistantMessage: messages.assistant,
          iteration,
        });
      }
    }

    return turns;
  }, [contentMessages]);

  // Check if this is a multi-turn conversation (more than one turn with messages)
  const isMultiTurnConversation = conversationTurns.length > 1;

  // Check if we should use hierarchical multi-agent rendering
  const hasMultipleAgents = useMemo(() => {
    if (!agentIterations) return false;
    return agentIterations.size > 1;
  }, [agentIterations]);

  // Use timeline-based rendering for AUTONOMOUS agent workflows
  // - Multi-agent workflows (any type)
  // - Single-agent autonomous workflows (non-conversational)
  // This shows: Trigger → Actions → Agent (with iterations) → Post-agent actions
  //
  // For CONVERSATIONAL multi-turn, use turn-based rendering instead (isMultiTurnConversation path)
  // This shows each turn: User message → Trigger/Actions (turn 1 only) → Iterations → Response
  const useTimelineRendering = useMemo(() => {
    // Multi-agent: always use timeline rendering
    if (hasMultipleAgents) return true;
    // Autonomous single-agent: use timeline rendering
    if (!isConversational && agentIterations && agentIterations.size >= 1) return true;
    // Conversational: use turn-based rendering (isMultiTurnConversation path)
    return false;
  }, [hasMultipleAgents, isConversational, agentIterations]);

  // For single-turn case, get first user message and last assistant response (legacy behavior).
  // When `useTimelineRendering` is true (autonomous workflows), the agent block itself
  // renders per-iteration user/assistant messages, so the legacy bracket would duplicate them.
  const initialUserMessage = !isMultiTurnConversation && !useTimelineRendering
    ? conversationTurns[0]?.userMessage
    : undefined;
  const finalAgentResponse = !isMultiTurnConversation && !useTimelineRendering && conversationTurns.length > 0
    ? conversationTurns[conversationTurns.length - 1]?.assistantMessage
    : undefined;

  // Helper to format chat message timestamp with date and time
  const formatChatTime = formatChatTimestamp;

  // Count failed for header
  const failedCount = useMemo(() => {
    return nodeExecutions.filter(e => e.status === 'failed').length;
  }, [nodeExecutions]);

  // For chatMessage workflows, count total actions across all turns (not just unique nodes)
  // This gives a more accurate count since the same action can execute multiple times
  const totalActionCount = useMemo(() => {
    // Check if this is a chatMessage workflow by looking for the orchestrator
    const isChatMessageWorkflow = checkIsChatMessageWorkflow(nodeExecutions);
    
    if (isChatMessageWorkflow && chatMessageIterationActions && chatMessageIterationActions.size > 0) {
      // Sum all actions across all iterations
      let total = 0;
      for (const [, actions] of chatMessageIterationActions) {
        total += actions.length;
      }
      return total;
    }
    
    // For non-chatMessage workflows, use the standard count
    return nodeExecutions.length;
  }, [nodeExecutions, chatMessageIterationActions]);

  // Scroll to and highlight selected item when selection changes (from canvas click)
  useEffect(() => {
    if (selectedNodeId && itemRefs.current.has(selectedNodeId)) {
      const element = itemRefs.current.get(selectedNodeId);
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      // Trigger highlight animation
      setHighlightedId(selectedNodeId);
      const timer = setTimeout(() => setHighlightedId(null), 500);
      return () => clearTimeout(timer);
    }
  }, [selectedNodeId]);

  // Decide which detail tabs to show. Drives both the visible <Tab> list and
  // the default-tab fallback below. Priority: link presence on the run payload
  // (hasInput/hasOutput) → resolved payload presence (covers tool actions and
  // ForEach/Until iterations that don't carry link metadata).
  const iterationInputForTabs = iteration.hasIterations && iteration.details
    ? iteration.details.input
    : undefined;
  const iterationOutputForTabs = iteration.hasIterations && iteration.details
    ? iteration.details.output
    : undefined;
  const selectedNodeInputForTabs = iterationInputForTabs ?? selectedNodeExecution?.input;
  const selectedNodeOutputForTabs = iterationOutputForTabs ?? selectedNodeExecution?.output;
  // Failed actions often have no inputsLink/outputsLink on the run payload
  // (the runtime omits them when the action errored before producing
  // content). The error code/message lives on `nodeExecution.error` and the
  // Output tab's renderer already falls back to displaying it — so when
  // there's an error we force both tabs visible so the user can read it.
  // Properties' "Check the output for error details" banner depends on the
  // Output tab being reachable.
  const hasError = !!(selectedToolAction?.error ?? selectedNodeExecution?.error);
  const showInputTab = selectedToolAction
    ? selectedToolAction.input !== undefined || hasError
    : selectedNodeExecution
      ? selectedNodeExecution.hasInput === true || selectedNodeInputForTabs !== undefined || hasError
      : true;
  const showOutputTab = selectedToolAction
    ? selectedToolAction.output !== undefined || hasError
    : selectedNodeExecution
      ? selectedNodeExecution.hasOutput === true || selectedNodeOutputForTabs !== undefined || hasError
      : true;

  // Retry history is carried on the node execution (not on tool actions). Only
  // show the tab when the engine actually retried the action.
  const retryHistory = selectedToolAction ? [] : selectedNodeExecution?.retryHistory ?? [];
  const hasRetryHistory = retryHistory.length > 0;

  // Re-pick a valid default tab when the selected action changes or when the
  // tab the user was viewing is no longer rendered. Priority mirrors the
  // rendered tab order (output -> input -> retry -> properties) so the fallback
  // stays consistent with what the user sees.
  const selectedKey = selectedToolAction?.actionName ?? selectedNodeExecution?.nodeId ?? null;
  useEffect(() => {
    const isSelectedAvailable =
      selectedTab === 'properties' ||
      (selectedTab === 'output' && showOutputTab) ||
      (selectedTab === 'input' && showInputTab) ||
      (selectedTab === 'retry' && hasRetryHistory);
    if (isSelectedAvailable) return;
    if (showOutputTab) setSelectedTab('output');
    else if (showInputTab) setSelectedTab('input');
    else if (hasRetryHistory) setSelectedTab('retry');
    else setSelectedTab('properties');
    // exhaustive-deps disabled because we intentionally do not depend on
    // `selectedTab`: the effect *sets* it, and including it would re-fire on
    // every user-driven tab change and overwrite their choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, showInputTab, showOutputTab, hasRetryHistory]);

  // Track retry-history tab views. Numeric attempt count goes in measurements.
  useEffect(() => {
    if (selectedTab === 'retry' && hasRetryHistory) {
      trackEvent(
        'execution.action.retryHistory.viewed',
        { actionName: selectedNodeExecution?.nodeId ?? null },
        { attempts: retryHistory.length },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, hasRetryHistory, selectedKey]);

  const handleCopy = async (content: unknown) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(content, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('Failed to copy to clipboard');
    }
  };

  const renderDetailContent = () => {
    // If a chat message is selected, show its content
    if (selectedChatMessage) {
      return (
        <div className={styles.jsonContainer}>
          <div className={styles.chatMessageHeader}>
            <Text weight="semibold">
              {selectedChatMessage.role === 'User' ? 'User Message' : 'Agent Response'}
            </Text>
            <Text size={200} className={styles.chatMessageTimestamp}>
              {formatChatTime(selectedChatMessage.timestamp)}
            </Text>
          </div>
          {/* Security: chat content is untrusted (user / agent generated)
              but rendered as a JSX text child inside <pre>, which React
              auto-escapes. Do not switch this to dangerouslySetInnerHTML
              or build raw HTML strings here — that would turn a crafted
              message into an XSS vector. */}
          <pre className={styles.jsonViewer}>
            {selectedChatMessage.content}
          </pre>
          <Tooltip content={copied ? 'Copied!' : 'Copy'} relationship="label">
            <Button
              className={styles.copyButton}
              icon={copied ? <CheckmarkFilled /> : <CopyFilled />}
              size="small"
              appearance="subtle"
              onClick={() => handleCopy(selectedChatMessage.content)}
              aria-label="Copy message to clipboard"
            />
          </Tooltip>
        </div>
      );
    }

    // Use tool action data if selected, otherwise use node execution
    // For ForEach inner actions, overlay repetition details when available
    const iterationInput = iteration.hasIterations && iteration.details ? iteration.details.input : undefined;
    const iterationOutput = iteration.hasIterations && iteration.details ? iteration.details.output : undefined;
    const displayData = selectedToolAction ? {
      input: selectedToolAction.input,
      output: selectedToolAction.output,
      status: selectedToolAction.status,
      startTime: selectedToolAction.startTime,
      endTime: selectedToolAction.endTime,
      duration: selectedToolAction.duration,
      nodeType: 'ToolAction',
      nodeId: selectedToolAction.actionName,
      error: selectedToolAction.error,
    } : selectedNodeExecution ? {
      input: iterationInput ?? selectedNodeExecution.input,
      output: iterationOutput ?? selectedNodeExecution.output,
      status: selectedNodeExecution.status,
      startTime: selectedNodeExecution.startTime,
      endTime: selectedNodeExecution.endTime,
      duration: selectedNodeExecution.duration,
      nodeType: selectedNodeExecution.nodeType,
      nodeId: selectedNodeExecution.nodeId,
      error: selectedNodeExecution.error,
      agentMetadata: selectedNodeExecution.agentMetadata,
    } : null;

    if (!displayData) {
      return (
        <div className={styles.emptyState}>
          <Text size={300}>Select an action</Text>
          <Text size={200}>from the list or canvas</Text>
        </div>
      );
    }

    // Helper to check if data is an error object from failed content fetch
    const isErrorObject = (data: unknown): data is { error: string } => {
      return typeof data === 'object' && data !== null && 'error' in data;
    };

    const isLoadingAny = isLoadingDetails || iteration.isLoadingAny;

    switch (selectedTab) {
      case 'input':
        if (isLoadingAny && !displayData.input) {
          return (
            <div className={styles.loadingState}>
              <Spinner size="small" />
              <Text>Loading...</Text>
            </div>
          );
        }
        if (!displayData.input) {
          // Show error message if available (e.g., template evaluation failed before inputs could be computed)
          if (displayData.error) {
            return (
              <div className={styles.errorContainer}>
                <Text weight="semibold" className={styles.errorCodeText}>
                  {displayData.error.code}
                </Text>
                <Text className={styles.preWrapText}>{displayData.error.message}</Text>
              </div>
            );
          }
          return (
            <div className={styles.emptyState}>
              <Text>No input data</Text>
            </div>
          );
        }
        // Check if input is an error object from failed content fetch
        if (isErrorObject(displayData.input)) {
          return (
            <div className={styles.emptyState}>
              <Text>{displayData.input.error}</Text>
            </div>
          );
        }
        return (
          <div className={styles.jsonViewerHost}>
            <JsonViewer data={displayData.input} downloadName="input" maxHeight="100%" />
          </div>
        );

      case 'output':
        if (isLoadingAny && !displayData.output) {
          return (
            <div className={styles.loadingState}>
              <Spinner size="small" />
              <Text>Loading...</Text>
            </div>
          );
        }
        if (!displayData.output) {
          // Show error message if available (e.g., action failed before producing output)
          if (displayData.error) {
            return (
              <div className={styles.errorContainer}>
                <Text weight="semibold" className={styles.errorCodeText}>
                  {displayData.error.code}
                </Text>
                <Text className={styles.preWrapText}>{displayData.error.message}</Text>
              </div>
            );
          }
          return (
            <div className={styles.emptyState}>
              <Text>No output data</Text>
            </div>
          );
        }
        // Check if output is an error object from failed content fetch
        if (isErrorObject(displayData.output)) {
          return (
            <div className={styles.emptyState}>
              <Text>{displayData.output.error}</Text>
            </div>
          );
        }
        return (
          <>
            {(displayData.agentMetadata?.modelType === 'FoundryAgentService' || displayData.agentMetadata?.modelType === 'FoundryAgentServiceV2') && (
              <div className={styles.foundryBanner}>
                <div className={styles.foundryIcon}>
                  <BotSparkleFilled />
                </div>
                <Text className={styles.foundryText}>
                  Foundry agent
                  {displayData.agentMetadata.threadId ? ` · Thread ${displayData.agentMetadata.threadId}` : ''}
                </Text>
                <Link
                  href={safeExternalUrl(foundryPortalUrl, FOUNDRY_FALLBACK_URL)}
                  target="_blank"
                  // Prevent the opened tab from reaching back via
                  // window.opener (tabnabbing) and stop the Referer
                  // header from leaking the current URL.
                  rel="noopener noreferrer"
                  className={styles.foundryLink}
                >
                  Open in Foundry <OpenRegular fontSize={12} />
                </Link>
              </div>
            )}
            <div className={styles.jsonViewerHost}>
              <JsonViewer data={displayData.output} downloadName="output" maxHeight="100%" />
            </div>
          </>
        );

      case 'properties':
        return (
          <>
            {displayData.status === 'failed' && (
              <div className={styles.errorSection}>
                <Text className={styles.errorTitle}>
                  Action failed. Check the output for error details.
                </Text>
              </div>
            )}
            <div className={styles.propertiesGrid}>
              <Text className={styles.propertyLabel}>Status</Text>
              <NodeStatusBadge status={displayData.status} />

              <Text className={styles.propertyLabel}>Start Time</Text>
              <Text className={styles.propertyValue}>
                {formatFullDateTime(displayData.startTime)}
              </Text>

              <Text className={styles.propertyLabel}>End Time</Text>
              <Text className={styles.propertyValue}>
                {formatFullDateTime(displayData.endTime)}
              </Text>

              <Text className={styles.propertyLabel}>Duration</Text>
              <Text className={styles.propertyValue}>
                {formatLogsDuration(displayData.duration)}
              </Text>

              <Text className={styles.propertyLabel}>Type</Text>
              <Text className={styles.propertyValue}>
                {displayData.nodeType}
              </Text>

              <Text className={styles.propertyLabel}>ID</Text>
              <Text className={styles.propertyValue}>
                {displayData.nodeId}
              </Text>
            </div>
          </>
        );

      case 'retry':
        return <RetryHistory attempts={retryHistory} />;

      default:
        return null;
    }
  };

  // Collapsed state - show thin vertical bar on right side
  if (isCollapsed) {
    return (
      <div className={styles.collapsedContainer}>
        <Tooltip content="Expand Logs" relationship="label" positioning="before">
          <Button
            className={styles.expandButton}
            appearance="subtle"
            size="small"
            icon={<ChevronLeftRegular />}
            onClick={() => onCollapseChange?.(false)}
            aria-label="Expand Logs"
          />
        </Tooltip>
        <TextBulletListSquareRegular className={styles.collapsedIcon} />
        <Text className={styles.collapsedTitle}>Logs</Text>
      </div>
    );
  }

  if (isLoadingRun) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <Spinner size="small" />
          <Text>Loading execution details…</Text>
        </div>
      </div>
    );
  }

  if (nodeExecutions.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <TextBulletListSquareRegular fontSize={32} className={styles.emptyStateIconSpacing} />
          <Text size={300} weight="semibold">No actions executed</Text>
          <Text size={200}>Actions will appear here when the workflow runs</Text>
        </div>
      </div>
    );
  }

  // Bundle of props shared between LogsPanel's inline execution log and
  // the MonitoringExpandDialog's spine, so both render the same tree from
  // the same data + state. Refs are intentionally NOT included — only the
  // LogsPanel instance needs scroll-into-view / keyboard nav tracking.
  const executionLogProps: ExecutionLogProps = {
    sortedExecutions,
    ...(chatHistory ? { chatHistory } : {}),
    ...(agentIterations ? { agentIterations } : {}),
    ...(toolActions ? { toolActions } : {}),
    ...(scopeRepetitions ? { scopeRepetitions } : {}),
    ...(scopeActions ? { scopeActions } : {}),
    ...(chatMessageIterationActions ? { chatMessageIterationActions } : {}),
    ...(canvasAgentIterations ? { canvasAgentIterations } : {}),
    ...(canvasScopeIterations ? { canvasScopeIterations } : {}),
    childrenByScope,
    conversationTurns,
    scopeParentByName,
    ...(loadingAgentIterations ? { loadingAgentIterations } : {}),
    ...(loadingToolActions ? { loadingToolActions } : {}),
    ...(loadingScopeRepetitions ? { loadingScopeRepetitions } : {}),
    ...(loadingScopeActions ? { loadingScopeActions } : {}),
    ...(loadingChatMessageIterations ? { loadingChatMessageIterations } : {}),
    useTimelineRendering,
    isMultiTurnConversation,
    isConversational,
    ...(initialUserMessage ? { initialUserMessage } : {}),
    ...(finalAgentResponse ? { finalAgentResponse } : {}),
    selectedNodeId,
    ...(selectedIterationIndex !== undefined ? { selectedIterationIndex } : {}),
    ...(selectedToolId !== undefined ? { selectedToolId } : {}),
    ...(selectedRepetitionName !== undefined ? { selectedRepetitionName } : {}),
    selectedChatMessage,
    highlightedId,
    setSelectedChatMessage,
    setSelectedToolAction,
    expandedAgents,
    expandedScopes,
    expandedRepetitions,
    toggleAgentExpanded,
    toggleScopeExpanded,
    toggleRepetitionExpanded,
    onNodeSelect,
    ...(onIterationChange ? { onIterationChange } : {}),
    ...(onRepetitionSelect ? { onRepetitionSelect } : {}),
    ...(onFetchToolActions ? { onFetchToolActions } : {}),
    ...(onFetchChatMessageIterationActions ? { onFetchChatMessageIterationActions } : {}),
    ...(onFetchScopeActions ? { onFetchScopeActions } : {}),
  };

  return (
    <div className={styles.container}>
      {/* Left column: Execution log header + execution list. Header and
          body live in the same column so the resize divider between
          columns spans the full panel height (header included). */}
      <div className={styles.column} style={{ width: listWidth, flexShrink: 0 }}>
        <div className={styles.columnHeader}>
          <div className={styles.headerLeft}>
            <Text className={styles.headerTitle}>Execution log</Text>
            <Text className={styles.headerCount}>
              {totalActionCount} action{totalActionCount !== 1 ? 's' : ''}
              {failedCount > 0 && ` · ${failedCount} failed`}
            </Text>
          </div>
          {onCollapseChange && (
            <Tooltip content="Collapse" relationship="label">
              <Button
                className={styles.collapseButton}
                appearance="subtle"
                size="small"
                icon={<ChevronRightRegular />}
                onClick={() => onCollapseChange(true)}
                aria-label="Collapse Logs"
              />
            </Tooltip>
          )}
        </div>
        {/* Execution list (tree structure) */}
        <ExecutionLog
          {...executionLogProps}
          listRef={listRef}
          itemRefs={itemRefs}
        />

        </div>

        {/* Draggable resize divider — spans the full panel height because
            it sits as a sibling of the column flex children, not nested
            inside an inner content row. */}
        <div
          className={mergeClasses(styles.resizeDivider, isResizing && styles.resizeDividerActive)}
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize execution list"
        />

        {/* Right column: tab strip header + details body. */}
        <div className={mergeClasses(styles.column, styles.detailsColumn)}>
          {!selectedChatMessage && (
            <div className={styles.columnHeader}>
              <div className={styles.tabsLeft}>
                <TabList
                  selectedValue={selectedTab}
                  onTabSelect={(_, data) => setSelectedTab(data.value as TabValue)}
                  size="small"
                >
                  {showOutputTab && <Tab value="output">Output</Tab>}
                  {showInputTab && <Tab value="input">Input</Tab>}
                  {hasRetryHistory && (
                    <Tab value="retry">Retry history ({retryHistory.length})</Tab>
                  )}
                  <Tab value="properties">Properties</Tab>
                </TabList>
              </div>
              {(selectedToolAction || selectedNodeExecution) && (
                <div className={styles.tabsRight}>
                  <NodeStatusBadge
                    status={selectedToolAction?.status ?? selectedNodeExecution?.status ?? 'pending'}
                  />
                  <Text className={styles.durationText}>{formatDuration(selectedToolAction?.duration ?? selectedNodeExecution?.duration)}</Text>
                  <Tooltip content="Expand action details" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<ArrowExpandRegular />}
                      aria-label="Expand action details"
                      onClick={() => {
                        trackEvent('monitoring_expand_dialog_button_clicked', {
                          source: 'tab_strip',
                          action_type: selectedNodeExecution?.nodeType ?? null,
                        });
                        setIsExpandOpen(true);
                      }}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          )}
          {/* Iteration selector for ForEach inner actions */}
          {iteration.hasIterations && (
            <IterationSelector
              repetitions={iteration.repetitions ?? []}
              selectedIndex={iteration.selectedIndex}
              onSelect={iteration.setSelectedIndex}
              isLoading={iteration.isLoading}
              totalDuration={(iteration.repetitions ?? []).reduce(
                (sum, r) => sum + (r.duration ?? 0),
                0,
              )}
            />
          )}
          {/* Content */}
          <div className={styles.content}>
            {renderDetailContent()}
          </div>
        </div>

      <MonitoringExpandDialog
        isOpen={isExpandOpen}
        onClose={() => setIsExpandOpen(false)}
        nodeExecutions={nodeExecutions}
        selectedNodeExecution={selectedNodeExecution}
        onSelectNode={(nodeId) => onNodeSelect(nodeId)}
        executionLog={{
          // Reuse all the rendered state, but strip the lazy-fetch callbacks.
          // ExecutionLog schedules fetches via setTimeout(0) during render
          // whenever it encounters an "expanded but not yet loaded" row.
          // Mounting a second instance (this dialog one) alongside the
          // inline panel would schedule duplicate fetches for the same
          // rows. The inline panel already owns those fetches and the
          // dialog sees the same loaded data the parent passes in.
          ...executionLogProps,
          onFetchToolActions: undefined,
          onFetchChatMessageIterationActions: undefined,
          onFetchScopeActions: undefined,
        }}
        {...(() => {
          // For ForEach scope repetitions, surface the iteration hook's
          // selected index so the dialog reflects the same repetition the
          // panel is showing. For agent iterations, fall back to the
          // parent-managed `selectedIterationIndex`.
          if (iteration.hasIterations) return { selectedIterationIndex: iteration.selectedIndex };
          if (typeof selectedIterationIndex === 'number') return { selectedIterationIndex };
          return {};
        })()}
        {...(() => {
          // Iteration total. Prefer ForEach scope repetition count; for
          // standard agent iterations, derive from the loaded
          // agentIterations data so the dialog's prev/next nav shows up
          // (the dialog's `hasIterations` check requires count > 1).
          if (iteration.repetitions && iteration.repetitions.length > 0) {
            return { iterationCount: iteration.repetitions.length };
          }
          if (selectedNodeExecution && isAgentExecution(selectedNodeExecution)) {
            const fetched = agentIterations?.get(selectedNodeExecution.nodeName);
            const count = fetched?.length ?? selectedNodeExecution.iterations?.length ?? selectedNodeExecution.iterationCount ?? 0;
            if (count > 1) return { iterationCount: count };
          }
          return {};
        })()}
        {...(() => {
          // Iteration wiring: prefer the ForEach scope repetitions handler
          // (drives `iteration.selectedIndex`) when the selected execution
          // has scope reps. Otherwise fall through to the parent's agent
          // iteration handler. Mixing the two would mutate the wrong
          // selection state.
          if (iteration.hasIterations) {
            return { onIterationChange: (idx: number) => iteration.setSelectedIndex(idx) };
          }
          if (onIterationChange && selectedNodeExecution) {
            // The parent handler builds selection state from the *agent
            // name* (it constructs `action-${agentName}` IDs); passing
            // `nodeId` here would double-prefix and desync the canvas
            // selection. Match the rest of the file by passing
            // `nodeName` (which equals the agent name for agent rows).
            const agentName = selectedNodeExecution.nodeName;
            return { onIterationChange: (idx: number) => onIterationChange(agentName, idx) };
          }
          return {};
        })()}
        {...(() => {
          // Resolve the input/output AND identity of the selected item so
          // the dialog drills into the same data + header the panel is
          // showing. Priority: selected tool action → ForEach iteration
          // overlay → node execution defaults (handled inside the dialog).
          if (selectedToolAction) {
            return {
              displayInput: selectedToolAction.input,
              displayOutput: selectedToolAction.output,
              displayName: selectedToolAction.actionName,
              displayStatus: selectedToolAction.status,
              ...(selectedToolAction.duration !== undefined ? { displayDuration: selectedToolAction.duration } : {}),
              ...(selectedToolAction.startTime ? { displayStartTime: selectedToolAction.startTime } : {}),
              ...(selectedToolAction.endTime ? { displayEndTime: selectedToolAction.endTime } : {}),
              displayNodeId: selectedToolAction.actionName,
              displayNodeType: 'ToolAction',
            };
          }
          if (iteration.details) {
            return {
              displayInput: iteration.details.input,
              displayOutput: iteration.details.output,
            };
          }
          return {};
        })()}
      />
    </div>
  );
};
